import React, { useContext, useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress } from '@mui/material';
import { LangContext } from '../../../context/LangContext';
import { ThemeContext } from '../../../context/ThemeContext';
import { Notification } from '../../UI/Notifications';
import { DropdownSelector } from '../../UI/DropdownSelector';
import { getAdmins } from '../../../utils/api/Admin/getAdminsApi';
import { updateLeadField } from '../../../utils/api/Lead/updateLead';
import { handleError } from '../../../utils/custom/errorHandler';
import styles from '../../../styles/components/Modal/Lead/LeadCommentModal.module.css';

const translations = {
    ru: {
        title: (leadName) => `Изменить менеджера для лида ${leadName}`,
        label: 'Менеджер',
        noManager: 'Без менеджера',
        save: 'Сохранить',
        saving: 'Сохранение...',
        cancel: 'Отмена',
        loadingError: 'Не удалось загрузить список менеджеров.',
        updateError: 'Не удалось обновить менеджера.',
    },
    en: {
        title: (leadName) => `Change manager for lead ${leadName}`,
        label: 'Manager',
        noManager: 'No manager',
        save: 'Save',
        saving: 'Saving...',
        cancel: 'Cancel',
        loadingError: 'Failed to load manager list.',
        updateError: 'Failed to update manager.',
    },
};

export const LeadManagerModal = ({ lead, role, onClose, onSuccess }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const currentTranslation = translations[langSelected] || translations.ru;

    const [selectedManager, setSelectedManager] = useState(lead?.manager?.id || lead?.manager_id || '');
    const [managerOptions, setManagerOptions] = useState([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const leadName = useMemo(() => {
        const fullName = `${lead?.first_name || ''} ${lead?.last_name || ''}`.trim();
        return fullName || `#${lead?.id ?? ''}`;
    }, [lead]);

    const selectorWidth = useMemo(() => {
        const labels = managerOptions.map((option) => String(option?.label || ''));
        const longestLabelLength = labels.reduce((max, label) => Math.max(max, label.length), 0);
        const widthInCh = Math.min(Math.max(longestLabelLength + 8, 20), 36);

        return `min(100%, ${widthInCh}ch)`;
    }, [managerOptions]);

    useEffect(() => {
        let isMounted = true;

        const loadManagers = async () => {
            setIsLoadingOptions(true);
            setErrors({});

            try {
                const perPage = 50;
                const firstPage = await getAdmins(1, perPage);
                const totalPages = Math.max(firstPage?.meta?.total_pages || 1, 1);
                let allAdmins = firstPage?.data || [];

                if (totalPages > 1) {
                    const pageRequests = [];

                    for (let page = 2; page <= totalPages; page += 1) {
                        pageRequests.push(getAdmins(page, perPage));
                    }

                    const pageResults = await Promise.all(pageRequests);
                    allAdmins = allAdmins.concat(pageResults.flatMap((result) => result?.data || []));
                }

                if (!isMounted) {
                    return;
                }

                const formattedManagers = [
                    { value: '', label: currentTranslation.noManager },
                    ...allAdmins.map((admin) => ({
                    value: admin.id,
                    label: (admin.first_name ? `${admin.first_name} ${admin.last_name || ''}`.trim() : admin.email) || '',
                    })),
                ];

                const currentManagerId = lead?.manager?.id ?? lead?.manager_id ?? '';
                const matchedManager = formattedManagers.find(
                    (option) => option.value?.toString() === currentManagerId?.toString()
                );

                setManagerOptions(formattedManagers);
                setSelectedManager(matchedManager ? matchedManager.value : '');
            } catch (error) {
                if (isMounted) {
                    setErrors({ general: currentTranslation.loadingError });
                }
            } finally {
                if (isMounted) {
                    setIsLoadingOptions(false);
                }
            }
        };

        loadManagers();

        return () => {
            isMounted = false;
        };
    }, [currentTranslation.loadingError, lead]);

    const handleManagerChange = (_, value) => {
        setSelectedManager(value);

        if (errors.general || errors.manager_id) {
            setErrors({});
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setErrors({});

        try {
            const managerValue = selectedManager === '' ? '' : parseInt(selectedManager, 10);
            await updateLeadField(lead.id, 'manager_id', managerValue, role);
            await onSuccess();
            onClose();
        } catch (error) {
            handleError(error.response?.data || error, setErrors);
            if (!error?.errors && !error?.response?.data?.errors) {
                setErrors((prev) => ({
                    ...prev,
                    general: prev.general || currentTranslation.updateError,
                }));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const managerErrorMessage = Array.isArray(errors.manager_id) ? errors.manager_id[0] : errors.manager_id;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div
                className={`${styles.modalContent} ${styles.statusModalContent} ${theme === 'dark' ? styles.darkContent : ''}`}
                onClick={(event) => event.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>{currentTranslation.title(leadName)}</h2>
                    <button className={styles.closeButton} onClick={onClose} type="button" aria-label="Close">
                        <CloseIcon />
                    </button>
                </div>

                <div className={`${styles.modalBody} ${styles.statusModalBody}`}>
                    {(errors.general || managerErrorMessage) && (
                        <Notification message={managerErrorMessage || errors.general} type="error" />
                    )}

                    <div
                        className={`${styles.selectorGroup} ${styles.statusSelectorGroup}`}
                        style={{ width: selectorWidth }}
                    >
                        <p className={`${styles.selectorLabel} ${theme === 'dark' ? styles.darkSelectorLabel : ''}`}>
                            {currentTranslation.label}
                        </p>

                        <DropdownSelector
                            filterName="manager_id"
                            selectedValue={selectedManager}
                            handleFilterChange={handleManagerChange}
                            options={managerOptions}
                            hasError={!!managerErrorMessage}
                            errorMessage={managerErrorMessage || ''}
                            disabled={isLoadingOptions || isSubmitting}
                        />
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <button
                        type="button"
                        className={`${styles.secondaryButton} ${theme === 'dark' ? styles.darkSecondaryButton : ''}`}
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        {currentTranslation.cancel}
                    </button>
                    <button
                        type="button"
                        className={`${styles.primaryButton} ${theme === 'dark' ? styles.darkPrimaryButton : ''}`}
                        onClick={handleSubmit}
                        disabled={isSubmitting || isLoadingOptions}
                    >
                        {isSubmitting ? <CircularProgress size={18} color="inherit" /> : currentTranslation.save}
                        {isSubmitting ? currentTranslation.saving : null}
                    </button>
                </div>
            </div>
        </div>
    );
};
